import clsx from "clsx";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";

/**
 * 공용 툴팁 — 데스크톱 hover, 터치 탭, 키보드 포커스 모두에서 열린다.
 *
 * <p>왜 필요한가: 기존 툴팁들은 CSS `group-hover:` 만 썼는데, 터치 기기에는 hover 가 없다.
 * 그래서 층 칩의 "마지막 업데이트", 가동률 막대의 그날 상세처럼 <strong>오직 툴팁에만 있는
 * 정보</strong>가 휴대폰에서는 아예 볼 수 없었다. 여기서 포인터 종류를 구분해 처리한다:</p>
 *
 * <ul>
 *   <li>마우스 — 올리면 열리고 벗어나면 닫힌다 (기존 동작 유지)</li>
 *   <li>터치/펜 — 탭하면 열리고, 다시 탭하거나 바깥을 누르면 닫힌다</li>
 *   <li>키보드 — 포커스로 열리고 ESC/블러로 닫힌다</li>
 * </ul>
 *
 * <p>트리거를 항상 {@code <button>} 으로 렌더하는 이유는 두 가지다. 포커스를 받을 수 있어야
 * 키보드로도 열리고, 터치 타깃 크기를 한 곳에서 보장할 수 있다.</p>
 */
export interface TipProps {
  /** 툴팁 본문. 여러 줄/리치 콘텐츠 가능. */
  content: ReactNode;
  /** 스크린리더용 한 줄 요약. 시각 콘텐츠만으로 의미가 안 통할 때 필수. */
  ariaLabel: string;
  /** 트리거(버튼)에 입힐 클래스 — 칩/슬롯 등 호출처의 기존 생김새를 그대로 유지한다. */
  className?: string;
  /**
   * 바깥 래퍼에 입힐 클래스. 래퍼가 기본 {@code inline-flex} 라, 부모 flex 안에서 폭을 나눠
   * 가져야 하는 트리거(가동률 막대 한 칸 등)는 여기에 {@code flex-1} 을 줘야 한다 —
   * 버튼에만 주면 부모가 보는 건 래퍼라 늘어나지 않는다.
   */
  wrapperClassName?: string;
  /** 툴팁 패널에 덧붙일 클래스 (폭, 줄바꿈 정책 등). */
  panelClassName?: string;
  /** 패널이 트리거 위/아래 중 어디에 뜰지. 기본 위. */
  placement?: "top" | "bottom";
  /** 패널 가로 정렬. 기본 왼쪽 맞춤. */
  align?: "start" | "center";
  /** 트리거 안에 보이는 내용. */
  children: ReactNode;
}

export default function Tip({
  content,
  ariaLabel,
  className,
  wrapperClassName,
  panelClassName,
  placement = "top",
  align = "start",
  children,
}: TipProps) {
  const [open, setOpen] = useState(false);
  /**
   * 터치로 열었는지 기억한다. 마우스로 연 경우는 leave 로 닫히지만, 터치는 leave 가 없어서
   * 바깥 탭을 듣고 있어야 닫을 수 있다 — 그 리스너를 필요할 때만 붙이기 위한 플래그.
   */
  const openedByTouch = useRef(false);
  const rootRef = useRef<HTMLSpanElement>(null);
  const panelId = useId();

  // 터치로 연 동안만 바깥 탭 감지. 마우스/키보드는 leave/blur 로 닫히므로 불필요.
  useEffect(() => {
    if (!open || !openedByTouch.current) return;
    const onDocPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        openedByTouch.current = false;
      }
    };
    document.addEventListener("pointerdown", onDocPointerDown);
    return () => document.removeEventListener("pointerdown", onDocPointerDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        openedByTouch.current = false;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <span ref={rootRef} className={clsx("relative inline-flex", wrapperClassName)}>
      <button
        type="button"
        aria-label={ariaLabel}
        aria-describedby={open ? panelId : undefined}
        aria-expanded={open}
        className={className}
        onPointerEnter={(e) => {
          if (e.pointerType === "mouse") setOpen(true);
        }}
        onPointerLeave={(e) => {
          if (e.pointerType === "mouse") setOpen(false);
        }}
        onPointerDown={(e) => {
          // 마우스는 hover 로 이미 열려 있다 — 클릭으로 닫아버리면 오히려 성가시다.
          if (e.pointerType === "mouse") return;
          openedByTouch.current = !open;
          setOpen((v) => !v);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          setOpen(false);
          openedByTouch.current = false;
        }}
      >
        {children}
      </button>

      {open && (
        <span
          id={panelId}
          role="tooltip"
          className={clsx(
            "pointer-events-none absolute z-30 rounded-md bg-slate-800 px-2.5 py-1.5 shadow-lg",
            "text-[11px] leading-relaxed text-white dark:bg-slate-700",
            placement === "top" ? "bottom-full mb-1.5" : "top-full mt-1.5",
            align === "center" ? "left-1/2 -translate-x-1/2" : "left-0",
            panelClassName,
          )}
        >
          {content}
        </span>
      )}
    </span>
  );
}
